/**
 * Calls to the API through the web app's gateway (/api/v1 on this domain). The session is
 * a cookie the gateway turns into a bearer token, so there's nothing to attach here.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: { path: string; message: string }[] = [],
  ) {
    super(message);
  }
}

const signedOutListeners = new Set<() => void>();

/** Called whenever the API says the session has ended. */
export function onSignedOut(listener: () => void): () => void {
  signedOutListeners.add(listener);
  return () => signedOutListeners.delete(listener);
}

type Options = { idempotencyKey?: string; signal?: AbortSignal };

/**
 * How far the server's clock is ahead of this phone's, from the Date header on each
 * response (to the second). Phones' clocks can be out; deadlines the API sets (an offer's
 * expiresAt) are turned into this phone's time with it.
 */
let clockOffsetMs = 0;

function noteServerTime(response: Response) {
  const date = Date.parse(response.headers.get("date") ?? "");
  // Half a second for the header's rounding down to the second.
  if (Number.isFinite(date)) clockOffsetMs = date + 500 - Date.now();
}

/** A time from the API (ISO 8601), as a time on this phone's clock (milliseconds). */
export const localTime = (iso: string): number => Date.parse(iso) - clockOffsetMs;

export async function api<T>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  options: Options = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (options.idempotencyKey) headers["idempotency-key"] = options.idempotencyKey;
  let response: Response;
  try {
    response = await fetch(`/api/v1${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch (error) {
    if ((error as Error).name === "AbortError") throw error;
    throw new ApiError(
      0,
      "offline",
      "You seem to be offline. Check your connection and try again.",
    );
  }
  noteServerTime(response);
  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // Not JSON: an error page from somewhere along the way.
  }
  if (!response.ok) {
    const error = (parsed as { error?: { code?: string; message?: string; details?: [] } } | null)
      ?.error;
    if (response.status === 401) for (const listener of signedOutListeners) listener();
    throw new ApiError(
      response.status,
      error?.code ?? "http_error",
      error?.message ?? "Something went wrong. Please try again.",
      error?.details ?? [],
    );
  }
  return parsed as T;
}

export const get = <T>(path: string, options?: Options) => api<T>("GET", path, undefined, options);
export const post = <T>(path: string, body?: unknown, options?: Options) =>
  api<T>("POST", path, body ?? {}, options);
export const patch = <T>(path: string, body: unknown) => api<T>("PATCH", path, body);
export const del = <T>(path: string) => api<T>("DELETE", path);

/** A message to show for anything thrown by an API call. */
/** A problem outside the API, with a message written for the customer. */
export class FriendlyError extends Error {}

export function errorMessage(error: unknown): string {
  if (error instanceof FriendlyError) return error.message;
  if (error instanceof ApiError) {
    const detail = error.details[0]?.message;
    return error.code === "validation_failed" && detail ? detail : error.message;
  }
  return "Something went wrong. Please try again.";
}

/** A key for one tap of a "book" or "tip" button, so a retry can't do it twice. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
